import React, { useContext, useEffect, useState, useCallback } from 'react';
import moment from 'moment';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Loader from '@amzn/meridian/loader';
import DataSource from '../../../api';
import DataState from '../../../Types/DataState';
import RenderDropdownsFromData from '../../../Util/Form/RenderDropdownsFromData';
import { styles } from './CAPReviewForm.styles';
import RadioButtonsFromData from '../../../Util/Form/RadioButtonsFromData';
import TimeoutToast from '../../CoachingForm/AutoSave/TimeoutToast';
import ContactReviewFeedbackModal from '../../ContactReview/Modal/FeedbackModal/ContactReviewFeedbackModal';
import { convertDailyTimestampToAmazonWeek } from '@amzn/aet-galactus-kit';
import {
  fetchContactStatus,
  getFormClickstreamTag1AndTag2,
  handleRealtimeHMDContact,
  OUTLIER_INGRESS,
} from '../utils/helper';
import { AUTOFILL_FIELDS } from '../../../Util/Form/Constants';
import { FormattedMessage, useIntl } from 'react-intl';
import { StateContext } from '../../../GlobalStateStore';
import { EmptyProfile, ExtendedUser, Profile } from '../../../Types';
import {
  getCurrentWeek,
  getCurrentWeekBoundriesList,
  renderUrl,
  urls,
} from '../../../Util';
import ProgressTracker, {
  ProgressTrackerStep,
} from '@amzn/meridian/progress-tracker';
import {
  FormSaveType,
  StepperProgressTypes,
  FormComponents,
  CapFormAutoFilledQuestions,
} from '../../../Util/Form/formData.constants';
import {
  ConsolidatedContact,
  ContactDetailsInput,
  ContactStatusOptionsObject,
  ContactViewTabObject,
  GoodRecommendationOptionsObject,
  I18NContent,
  RecommendedContactStatusOptionsObject,
} from '../../../api/CoachingNotes/schema';
import {
  DropdownItem,
  Section,
  Question,
  RadioButtonType,
  StepperQuestionsConfig,
  CAPPageItem,
  CAPAnsweredQuestionItem,
  QuestionOption,
} from '../../../Util/Form/formData.types';
import {
  grabExtraFieldByID,
  getDefaultMessageByID,
  mergeFeedbackIntoContactDetails,
} from '../../../Util/Form/helpers';
import RenderGenericDropdownsFromData from '../../../Util/Form/RenderGenericDropdownsFromData';
import RenderGenericRadioButtonsFromData from '../../../Util/Form/RenderGenericRadioButtonsFromData';
import RenderGenericTextAreaFromData from '../../../Util/Form/RenderGenericTextFromData';
import {
  DynamicFormAnswerInput,
  FormDetailsInput,
  I18NContentInput,
  AutofillDetails,
} from '../../../api/FormLibrary/schema';
import { getLocaleString } from '../../../translations';
import { ContactStatus } from '../../Dashboard/DashboardComponents/HmdDetails/types';
import { EmptyContactStatus } from '../../Dashboard/DashboardComponents/HmdDetails/helper';
import { useNavigate } from 'react-router';
import DraftSavedToast from '../../../Util/Form/DraftSavedToast';
import { Feature, isFeatureEnabled } from '../../../config/FeaturesConfig';

interface CAPReviewFormState {
  dropdownItems: DropdownItem[];
  radioButtonItems: RadioButtonType[];
  stepperProgress: { [key: string]: StepperProgressTypes };
  pages: CAPPageItem[];
  currentPage: number;
  questionsAnswered: { [key: string]: CAPAnsweredQuestionItem };
  nextButtonDisabled: boolean;
  stepperQuestionsConfig: StepperQuestionsConfig;
  contactInfo: ConsolidatedContact;
  nextButtonRefresh: number;
}

interface CAPReviewFormProps {
  userId: string | undefined;
  contactId: string | undefined;
  selectedVertical: string;
  formType: string;
  ccxDetailsInfo: any;
  ingress: string | null;
  passedProfile?: ExtendedUser;
  passedContactInfo?: ConsolidatedContact;
}

type FormInformation = {
  stepperQuestionsConfig: { [key: string]: StepperQuestionsConfig };
  defaultDropdownItems: DropdownItem[];
};

const csaFields = (
  type: string,
  subtype: string,
  contentID: string,
  ingress: string | null,
  isCCX?: boolean
) => ({
  'data-csa-c-type': type,
  'data-csa-c-slot-id': `'tx-monitoring-${subtype}-button-slot'`,
  'data-csa-c-content-id': `tx-monitoring-${subtype}-button-content-id-${contentID}`,
  ...getFormClickstreamTag1AndTag2(ingress, isCCX),
  'data-csa-c-tag3': `Transaction Monitoring`,
});

// GGGG refers to the year of the week. This may be different from YYYY
// When part of the week is from 2024 and part of it is from 2025. e.g. 2024-12-30 (Monday)
// In such cases, GGGG refers to the new year (e..g 2025) and YYYY refers to the previous year (e.g. 2024).
const startWeek = moment().subtract(4, 'w').format('GGGG-WW');
const endWeek = moment().format('GGGG-WW');
const date = getCurrentWeekBoundriesList();

const CAPReviewForm: React.FC<CAPReviewFormProps> = ({
  userId,
  contactId,
  selectedVertical,
  formType,
  ccxDetailsInfo,
  passedProfile,
  ingress,
  passedContactInfo,
}) => {
  const globalState = useContext(StateContext);
  const navigate = useNavigate();
  const { inspireOptions } = globalState?.state;
  const initialProfile = passedProfile
    ? { ...EmptyProfile, user: passedProfile }
    : EmptyProfile;
  const [profile, setProfile] = useState<Profile>(initialProfile);
  const [, setProfileState] = useState(DataState.LOADING);
  const [stepperKeys, setStepperKeys] = useState<string[]>([]);
  const [CAPFormData, setCAPFormData] = useState<any>(undefined);
  const [CAPFormDataState, setCAPFormDataState] = useState(DataState.LOADING);
  const [nextButtonRefresh, setNextButtonRefresh] = useState(0);
  const [contactIDs, setContactIDs] = useState<string[]>([]);
  const [contactList, setContactList] = useState<ConsolidatedContact[]>([]);
  const [contactListState, setContactListState] = useState(DataState.LOADING);
  const [contactStatusDetails, setContactStatusDetails] =
    useState<ContactStatus>(EmptyContactStatus);
  const [contactStatusDetailsState, setContactStatusDetailsState] = useState(
    DataState.LOADING
  );
  const [formInformation, setFormInformation] = useState<
    Partial<FormInformation>
  >({});
  const [formStatusCode, setFormStatusCode] = useState({});
  const [formStatusCodeState, setFormStatusCodeState] = useState(
    DataState.INITIALIZED
  );
  const [outlierResp, setOutlierResp] = useState({});
  const [outlierRespState, setOutlierRespState] = useState(
    DataState.INITIALIZED
  );
  const [requiredQuestionCount, setRequiredQuestionCount] = useState<number>(0);
  const [formState, setFormState] = useState<Partial<CAPReviewFormState>>({
    dropdownItems: [],
    radioButtonItems: [],
    currentPage: 0,
    nextButtonDisabled: true,
    contactInfo: passedContactInfo,
  });
  const [showContactReviewFeedbackModal, setShowContactReviewFeedbackModal] =
    useState<{ open: boolean; status?: FormSaveType }>({
      open: false,
    });

  // Toast
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [showDraftSavedToast, setShowDraftSavedToast] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const [exceptionMessage, setExceptionMessage] = useState('');

  const apiNaws = DataSource.getDataFeed('NAWS');
  const { cscEntityPrefix, isAdmin, requesterUserID } = inspireOptions.data;
  const intl = useIntl();

  useEffect(() => {
    setFormState(prevState => ({
      ...prevState,
      contactInfo:
        prevState.contactInfo ??
        contactList?.filter(c => c.contactID === contactId)?.[0] ??
        {},
      dropdownItems: handleSpecialDropdownItems(
        formState?.dropdownItems ?? [],
        CAPFormData?.[0]
      ),
    }));
  }, [CAPFormData, contactId, selectedVertical]);

  useEffect(() => {
    if (
      formStatusCodeState === DataState.ERROR ||
      outlierRespState === DataState.ERROR
    ) {
      setShowErrorToast(true);
    }
    if (formStatusCodeState === DataState.LOADED && isSavingDraft) {
      setShowDraftSavedToast(true);
    }
    setIsSavingDraft(false);
  }, [formStatusCodeState, outlierRespState]);

  useEffect(() => {
    if (
      inspireOptions.state === DataState.LOADED &&
      userId &&
      selectedVertical !== ''
    ) {
      apiNaws.getFormTemplateData(
        { VERTICAL: selectedVertical },
        setCAPFormData,
        setCAPFormDataState
      );
    }
  }, [inspireOptions.state, userId, selectedVertical]);

  // Get profile data for user on page load
  useEffect(() => {
    if (inspireOptions.state === DataState.LOADED && userId) {
      if (!passedProfile) {
        const queryMigrationVariables = apiNaws.prepareQuery(
          getCurrentWeek(),
          userId,
          cscEntityPrefix
        );
        apiNaws.getProfileData(
          queryMigrationVariables,
          setProfile,
          setProfileState
        );
      }
    }
  }, [inspireOptions.state, userId]);

  // After getConsolidatedContacts query finishes, update formState with contactInfo
  useEffect(() => {
    if (contactList?.length > 0) {
      if (contactId) {
        const contactInfo = contactList?.find(c => c.contactID === contactId);

        setFormState(prevFormState => ({
          ...prevFormState,
          contactInfo,
          questionsAnswered: {
            ...prevFormState.questionsAnswered,
          },
        }));
      }
      setContactIDs(contactList.map(c => c.contactID));
    }
  }, [contactList]);

  const handleSpecialDropdownItems = (
    dropdownItems: DropdownItem[],
    data: any
  ): DropdownItem[] => {
    dropdownItems = dropdownItems?.map((d: DropdownItem) => {
      switch (d.type) {
        case 'cap_review_form_contact_review_form_type':
          d.selected = formType;
          break;
      }
      return d;
    });
    return dropdownItems;
  };

  // This just ensures the vertical dropdown has a selected value in the formState object
  useEffect(() => {
    const items = formState?.dropdownItems;
    if (items && selectedVertical) {
      const index = items.findIndex(t => t.type === 'vertical');
      const newItem = { ...items[index], selected: selectedVertical };
      items[index] = newItem;

      setFormState(prevFormState => ({
        ...prevFormState,
        dropdownItems: items,
      }));
    }
  }, [formState.dropdownItems, selectedVertical]);

  // Set the initial formState from operations
  useEffect(() => {
    const stepperProgress = Object.fromEntries(
      stepperKeys.map((key, index) => [
        key,
        index === 0
          ? StepperProgressTypes.PRESENT
          : StepperProgressTypes.FUTURE,
      ])
    );

    const pages: CAPPageItem[] = [];

    stepperKeys.forEach((key, index) => {
      const points = formInformation?.stepperQuestionsConfig?.[key]?.points;

      if (!points) {
        return;
      }

      const page = points.map(point => ({
        question: point,
        required: point?.REQUIRED ?? false,
      }));

      // If this is the first page, it is by default loaded, therefore can be considered as visited
      pages.push({ points: page, type: key, visited: index === 0 });
    });

    setFormState(prevFormState => ({
      ...prevFormState,
      stepperProgress,
      pages,
    }));
  }, [formInformation]);

  // Every time a question is answered or the nextButton is refreshed (usually by a click), this method reevaluates the left stepper bar statuses and the nextButtonRefresh
  useEffect(() => {
    const { currentPage, pages, questionsAnswered } = formState;
    if (currentPage !== undefined && pages && questionsAnswered) {
      setFormState(prevFormState => ({
        ...prevFormState,
        nextButtonRefresh,
      }));

      updateStepperProgress();
    }
  }, [formState.questionsAnswered, nextButtonRefresh]);

  // This function includes a lot of logical overhead that shouldn't be computed everytime.
  // Example - we shouldn't always compute a map of required questions, consider moving this to formState or some other variable
  const updateStepperProgress = () => {
    // Make a mapping of each page name to a list of that page's required questions
    const requiredPointsPerPage: {
      [pageType: string]: { requiredQuestions: string[]; visited: boolean };
    } = {};
    (formState?.pages ?? []).forEach(p => {
      requiredPointsPerPage[p.type] = {
        requiredQuestions: p.points
          .filter(point => point.required)
          .map(point => point.question.QUESTION_TEXT.DEFAULT_MESSAGE),
        visited: p.visited ?? false,
      };
    });

    const stepperProgressMap: { [key: string]: StepperProgressTypes } = {};
    let isCurrentPageComplete = false;
    Object.keys(requiredPointsPerPage).forEach((pageType, index) => {
      const requiredQuestions = requiredPointsPerPage[pageType];

      // Check if all required questions for this page are answered
      const allRequiredAnswered = requiredQuestions.requiredQuestions.every(
        questionText =>
          formState?.questionsAnswered?.[questionText] !== undefined &&
          formState.questionsAnswered[questionText].checkedValue !== undefined
      );

      // While looping over all pages with an index value
      // Double check whether or no the current page is complete
      if (formState?.currentPage === index && allRequiredAnswered) {
        isCurrentPageComplete = true;
      }

      // Always set current page to PRESENT
      if (formState?.currentPage === index) {
        stepperProgressMap[pageType] = StepperProgressTypes.PRESENT;

        // If we haven't visited the page, FUTURE
      } else if (!requiredPointsPerPage[pageType].visited) {
        stepperProgressMap[pageType] = StepperProgressTypes.FUTURE;
      } else {
        if (allRequiredAnswered) {
          stepperProgressMap[pageType] = StepperProgressTypes.PAST;
        } else {
          stepperProgressMap[pageType] = StepperProgressTypes.FUTURE;
        }
      }
    });

    setFormState({
      ...formState,
      stepperProgress: stepperProgressMap,
      nextButtonDisabled: !isCurrentPageComplete,
    });
  };

  // First useEffect called after fetching the Form Template from FORM_SYSTEM
  useEffect(() => {
    if (!CAPFormData?.[0]) return;
    let defaultDropdownItems =
      CAPFormData[0].SECTIONS.find(
        (section: Section) =>
          section.SECTION_ID === 'GENERAL_PARAMETERS_DROPDOWNS'
      )?.QUESTIONS.map((question: Question) => ({
        type: question.QUESTION_ID.toLowerCase(),
        placeholder: question.QUESTION_TEXT,
        options: question.QUESTION_OPTIONS || [],
        selected: undefined,
      })) || [];

    defaultDropdownItems = handleSpecialDropdownItems(
      defaultDropdownItems,
      CAPFormData[0]
    );

    let requiredCounter = 0;
    const stepperQuestionsConfig: { [key: string]: StepperQuestionsConfig } =
      CAPFormData[0].SECTIONS.filter(
        (section: Section) => section.TYPE === 'MULTITYPE'
      ).reduce((acc: any, section: any) => {
        if (section?.QUESTION_TEXT?.DEFAULT_MESSAGE) {
          const points = section.QUESTIONS;

          // Go through each question in section.QUESTIONS and if you see an *, add a REQUIRED attribute  true, else false
          // This should move to backend - update schemas to include required field, and update gql API to request REQUIRED field
          const hydratedPoints = points.map((point: any) => {
            const required = point.QUESTION_TEXT.DEFAULT_MESSAGE.includes('*');
            if (required) requiredCounter++;
            return {
              ...point,
              REQUIRED: required,
            };
          });

          acc[section.QUESTION_TEXT.DEFAULT_MESSAGE] = {
            points: hydratedPoints,
            trackLength:
              section.QUESTIONS.length <= 2
                ? 6
                : Math.ceil(section.QUESTIONS.length / 2) * 5,
            customClass: '',
          };
        }
        return acc;
      }, {});

    setRequiredQuestionCount(requiredCounter);

    setFormInformation(prevFormInformation => ({
      ...prevFormInformation,
      defaultDropdownItems,
      stepperQuestionsConfig,
    }));

    setStepperKeys(Object.keys(stepperQuestionsConfig));

    setFormState(prevFormState => ({
      ...prevFormState,
      dropdownItems: defaultDropdownItems,
    }));
  }, [CAPFormData]);

  // This Use Effect performs the logic to support auto-pupulate value after saving
  useEffect(() => {
    if (contactId) {
      const contactInfo = contactList.find(
        contact => contact.contactID === contactId
      );

      if (contactInfo) {
        const formReviews = contactInfo?.formReviews?.filter(
          f => f.formID === 'CAP_REVIEW_FORM'
        );

        const questionsAnswered = formReviews?.[0]?.formAnswer.reduce(
          (
            acc: { [key: string]: CAPAnsweredQuestionItem },
            answer: DynamicFormAnswerInput
          ) => {
            // If both default message + value are undefined - this is a text field
            if (!answer.selectedOption?.defaultMessage && !answer.value) {
              acc[answer.questionText.defaultMessage] = {
                checkedValue: answer.answerText,
                numValue: undefined,
              };
            } else {
              acc[answer.questionText.defaultMessage] = {
                checkedValue: answer.selectedOption?.defaultMessage,
                numValue: answer.value,
              };
            }
            return acc;
          },
          {}
        );

        const dropdowns = formState?.dropdownItems;
        (dropdowns ?? []).forEach((item: DropdownItem) => {
          const answeredQuestion =
            questionsAnswered?.[item.placeholder.DEFAULT_MESSAGE];
          if (answeredQuestion) {
            item.selected = answeredQuestion?.checkedValue?.toString();
            delete questionsAnswered[item.placeholder.DEFAULT_MESSAGE];
          }
        });

        const radios = formState?.radioButtonItems;
        (radios ?? []).forEach((item: RadioButtonType) => {
          const answeredQuestion =
            questionsAnswered?.[item.placeholder.DEFAULT_MESSAGE];
          if (answeredQuestion) {
            item.checked = answeredQuestion.numValue;
            delete questionsAnswered[item.placeholder.DEFAULT_MESSAGE];
          }
        });

        Object.entries(questionsAnswered ?? {}).forEach(([key, question]) => {
          if (
            typeof question.checkedValue === 'string' &&
            typeof question.numValue === 'number'
          ) {
            question.checkedValue = question.numValue;
          }
        });

        updateStepperProgress();

        setFormState(prevFormState => ({
          ...prevFormState,
          dropdownItems: dropdowns,
          radioButtonItems: radios,
          questionsAnswered: questionsAnswered,
        }));
      }
    }
  }, [contactId, contactList]);

  useEffect(() => {
    fetchContactStatus(
      ingress,
      profile,
      cscEntityPrefix,
      date,
      apiNaws,
      setContactStatusDetails,
      setContactStatusDetailsState
    );
  }, [ingress, profile]);

  useEffect(() => {
    handleRealtimeHMDContact(
      contactId,
      ingress,
      contactList,
      contactListState,
      contactStatusDetailsState,
      profile,
      cscEntityPrefix,
      contactStatusDetails,
      setContactList,
      setContactListState
    );
  }, [
    contactId,
    ingress,
    contactList,
    profile,
    contactListState,
    contactStatusDetailsState,
  ]);

  // After fetching profile with getProfileData, query getConsolidatedContacts with profile information
  useEffect(() => {
    refreshContacts();
  }, [profile]);

  const refreshContacts = useCallback(() => {
    const { userName } = profile.user;
    if (userName !== '') {
      const userID = userId ? `${cscEntityPrefix}:${userId}` : '';
      const queryVariablesNaws = apiNaws.prepareQueryforRecommendationsGetter(
        userID,
        userName,
        startWeek,
        endWeek
      );
      apiNaws.getConsolidatedContacts(
        queryVariablesNaws,
        setContactList,
        setContactListState
      );
    }
  }, [profile, userId]);

  const handleSelectDropdownValue = (type: string) => (selected: string) => {
    const items = formState?.dropdownItems;
    if (items) {
      const index = items.findIndex(t => t.type === type);
      const newItem = { ...items[index], selected };
      items[index] = newItem;
      if (items[index].type?.includes('vertical')) {
        items[index].selected = selectedVertical;
      }
      setFormState(prevFormState => ({
        ...prevFormState,
        dropdownItems: items,
      }));
    }
  };

  const getStepperLabel = (key: string) => (
    <span>
      <FormattedMessage id='cs_perfportal_cap_label' defaultMessage={key} />
    </span>
  );

  const handleQuestionAnswered =
    (questionText: string) => (answer: CAPAnsweredQuestionItem) => {
      setFormState(prevFormState => ({
        ...prevFormState,
        questionsAnswered: {
          ...prevFormState.questionsAnswered,
          [questionText]: answer,
        },
      }));
    };

  const handleClickBackButton = () => {
    const { currentPage } = formState;
    if (!currentPage) {
      return;
    }
    setFormState(prevFormState => ({
      ...prevFormState,
      currentPage: currentPage - 1,
    }));
  };

  const handleFeedbackSubmitted = async (
    feedbackDetails: ContactDetailsInput
  ): Promise<any> => {
    const status = showContactReviewFeedbackModal?.status;
    setShowContactReviewFeedbackModal({
      open: false,
      status: undefined,
    });

    switch (status) {
      case FormSaveType.DELIVERED:
        await handleSave(feedbackDetails);
        break;
      case FormSaveType.DRAFT:
      default:
        await handleSaveAsDraft();
        break;
    }

    let redirectUrl = `${urls.coachingNotes}/${userId}?href=${ContactViewTabObject.REVIEWED}`;

    if (ingress === 'realtimeHMD') {
      redirectUrl += `&ingress=realtimeHMD`;
    } else if (ingress === OUTLIER_INGRESS) {
      redirectUrl = urls.outlierManagement;
    }

    setTimeout(() => {
      navigate(renderUrl(redirectUrl), { replace: true });
    }, 3000);
  };

  const handleClickNextPage = () => {
    const { currentPage, pages, stepperProgress } = formState;
    if (currentPage !== undefined && pages && stepperProgress) {
      const newStepperProgress = { ...stepperProgress };
      const currentPageType = pages[currentPage].type;
      if (currentPage === pages.length - 1) {
        // On the last page, only need to set the last stepper to past
        newStepperProgress[currentPageType] = StepperProgressTypes.PAST;
        setFormState(prevFormState => ({
          ...prevFormState,
          stepperProgress: newStepperProgress,
        }));
        return;
      }

      const nextPage = currentPage + 1;
      pages[nextPage].visited = true;
      const newPageType = pages[nextPage].type;
      // If the page changed the stepper step, we need to update that as well
      if (newPageType !== currentPageType) {
        newStepperProgress[currentPageType] = StepperProgressTypes.PAST;
        newStepperProgress[newPageType] = StepperProgressTypes.PRESENT;
      }
      setFormState(prevFormState => ({
        ...prevFormState,
        pages: pages,
        currentPage: nextPage,
        stepperProgress: newStepperProgress,
      }));
      setNextButtonRefresh(nextButtonRefresh + 1);
    }
  };

  const getFormAnswers = (
    questionsAnswered: { [key: string]: CAPAnsweredQuestionItem } | undefined,
    sectionKey: string,
    SECTIONS: Section[]
  ): DynamicFormAnswerInput[] => {
    const formAnswers: DynamicFormAnswerInput[] = [];
    const section: Section | undefined = SECTIONS.find(
      (section: Section) => section.SECTION_ID === sectionKey
    );

    (section?.QUESTIONS ?? []).forEach(question => {
      if (
        questionsAnswered?.[question.QUESTION_TEXT.DEFAULT_MESSAGE]
          ?.checkedValue !== undefined
      ) {
        const checkedValue: string =
          questionsAnswered[
            question.QUESTION_TEXT.DEFAULT_MESSAGE
          ].checkedValue?.toString() ?? '';

        const questionText: I18NContentInput = {
          translationID: question.QUESTION_TEXT.TRANSLATION_ID,
          defaultMessage: question.QUESTION_TEXT.DEFAULT_MESSAGE,
        };

        let formDataItem: DynamicFormAnswerInput = {
          questionID: question.QUESTION_ID,
          questionText,
        };

        if (question.QUESTION_TYPE === 'FREETEXT') {
          formDataItem = {
            ...formDataItem,
            answerText: checkedValue,
          };
        } else {
          const checkedValueInt = parseInt(checkedValue, 10);
          const selectedOption: QuestionOption | undefined =
            question?.QUESTION_OPTIONS?.find(
              (option: QuestionOption) => option.VALUE === checkedValueInt
            );

          formDataItem = {
            ...formDataItem,
            selectedOption: {
              translationID: selectedOption?.TRANSLATION_ID ?? '',
              defaultMessage: selectedOption?.DEFAULT_MESSAGE ?? '',
            },
            value: checkedValueInt,
          };
        }

        formAnswers.push(formDataItem);
      }
    });
    return formAnswers;
  };

  const handleSaveAsDraft = () => handleSaveLogic(FormSaveType.DRAFT);
  const handleSave = async (feedbackDetails: ContactDetailsInput) => {
    if (!canSave) {
      return;
    }
    handleSaveLogic(FormSaveType.DELIVERED, feedbackDetails);
  };

  const handleSaveLogic = async (
    status: FormSaveType,
    feedbackDetails?: ContactDetailsInput
  ) => {
    const {
      dropdownItems = [],
      questionsAnswered,
      radioButtonItems = [],
    } = formState;
    const {
      FORM_ID: formID,
      VERSION: version,
      SECTIONS,
      AUTOFILL,
      TITLE: title,
    } = CAPFormData?.[0];
    const formAnswers: DynamicFormAnswerInput[] = [];

    dropdownItems.forEach((item: DropdownItem) => {
      const section: Section | undefined = SECTIONS.find(
        (section: Section) =>
          section.SECTION_ID === 'GENERAL_PARAMETERS_DROPDOWNS'
      );

      const question: Question | undefined = section?.QUESTIONS.find(
        (question: Question) =>
          question.QUESTION_ID === item.type?.toUpperCase()
      );

      if (question) {
        const questionText: I18NContentInput = {
          translationID: question.QUESTION_TEXT.TRANSLATION_ID,
          defaultMessage: question.QUESTION_TEXT.DEFAULT_MESSAGE,
        };

        let selectedOption = item.selected;

        if (questionText.defaultMessage.includes('Vertical')) {
          selectedOption = selectedVertical;
        }
        let selectedOptionText = question.QUESTION_OPTIONS?.find(
          option => option.DEFAULT_MESSAGE === selectedOption
        );

        let selectedOptionInput: I18NContentInput | undefined;
        if (selectedOptionText) {
          selectedOptionInput = {
            translationID: selectedOptionText.TRANSLATION_ID,
            defaultMessage: selectedOptionText.DEFAULT_MESSAGE,
          };
        } else if (question.QUESTION_ID === 'CONTACT_REVIEW_FORM_TYPE') {
          selectedOptionInput = {
            defaultMessage: 'CAP Review Form',
            translationID: 'cs_perfportal_cap_form_title',
          };
        } else {
          selectedOption = undefined;
        }

        const valueOfOption =
          selectedOptionText &&
          selectedOptionText.VALUE !== undefined &&
          selectedOptionText.VALUE !== null
            ? { value: selectedOptionText.VALUE }
            : {};

        formAnswers.push({
          questionID: question.QUESTION_ID,
          questionText,
          selectedOption: selectedOptionInput,
          ...valueOfOption,
        });
      }
    });

    radioButtonItems.forEach((item: RadioButtonType) => {
      const section: Section | undefined = SECTIONS.find(
        (section: Section) =>
          section.SECTION_ID === 'CAP_GENERAL_PARAMETERS_RADIO'
      );

      const question: Question | undefined = section?.QUESTIONS.find(
        (question: Question) =>
          question.QUESTION_ID === item.type?.toUpperCase()
      );

      if (question) {
        const questionText: I18NContentInput = {
          translationID: question.QUESTION_TEXT.TRANSLATION_ID,
          defaultMessage: question.QUESTION_TEXT.DEFAULT_MESSAGE,
        };

        let selectedOption = item.checked;
        if (item.checked == undefined) return;

        let selectedOptionText = question.QUESTION_OPTIONS?.find(
          option => option.VALUE === selectedOption
        );

        let selectedOptionInput: I18NContentInput | undefined;
        if (selectedOptionText) {
          selectedOptionInput = {
            translationID: selectedOptionText.TRANSLATION_ID,
            defaultMessage: selectedOptionText.DEFAULT_MESSAGE,
          };
        } else {
          selectedOption = undefined;
        }

        const valueOfOption =
          selectedOptionText &&
          selectedOptionText.VALUE !== undefined &&
          selectedOptionText.VALUE !== null
            ? { value: selectedOptionText.VALUE }
            : {};

        formAnswers.push({
          questionID: question.QUESTION_ID,
          questionText,
          selectedOption: selectedOptionInput,
          ...valueOfOption,
        });
      }
    });

    const questionSections: string[] = [
      'CAP_SIGNAL_PARAMETERS',
      'CAP_POLICY_APPLICATION',
      'CON_LW_ANNO_SIC',
      'CAP_COMMUNICATION_PARAMETERS',
      'CAP_CLOSING_PARAMETERS_COMPLIANCE',
      'CAP_CLOSING_PARAMETERS_OCV_PA_TD',
    ];

    questionSections.forEach(section => {
      formAnswers.push(...getFormAnswers(questionsAnswered, section, SECTIONS));
    });

    const autofillMapping = new Map<string, string>();
    AUTOFILL.forEach((entry: AutofillDetails) => {
      autofillMapping.set(entry.TS_KEY, entry.QUESTION_ID);
    });

    AUTOFILL_FIELDS.forEach((field: string) => {
      const cap: any = formState?.contactInfo?.capFields ?? {};
      if (cap[field] !== undefined) {
        formAnswers.push({
          questionID: autofillMapping.get(field) ?? '',
          questionText: {
            translationID: autofillMapping.get(field) ?? '',
            defaultMessage: autofillMapping.get(field) ?? '',
          },
          answerText: cap[field] ?? undefined,
        });
      }
    });

    formAnswers.push({
      questionID: autofillMapping.get('auditWeek') ?? '',
      questionText: {
        translationID: autofillMapping.get('auditWeek') ?? '',
        defaultMessage: autofillMapping.get('auditWeek') ?? '',
      },
      answerText: convertDailyTimestampToAmazonWeek(
        moment().format('YYYY-MM-DD')
      ),
    });

    formAnswers.push({
      questionID: autofillMapping.get('tm') ?? '',
      questionText: {
        translationID: autofillMapping.get('tm') ?? '',
        defaultMessage: autofillMapping.get('tm') ?? '',
      },
      answerText: inspireOptions.data.requesterUsername,
    });

    formAnswers.push({
      questionID: autofillMapping.get('channel') ?? '',
      questionText: {
        translationID: autofillMapping.get('channel') ?? '',
        defaultMessage: autofillMapping.get('channel') ?? '',
      },
      answerText: passedContactInfo?.channel,
    });

    const formDetailsInput: FormDetailsInput = {
      formID: formID,
      version: version,
      formAnswers,
      contactStatus: status,
    };

    const selectedContact = contactList.filter(
      c => c.contactID === contactId
    )[0];
    const vertical = formState?.dropdownItems?.find(
      d => d.type === 'vertical'
    )?.selected;

    const contactDetailsInput = mergeFeedbackIntoContactDetails(
      feedbackDetails,
      selectedContact!,
      vertical,
      formDetailsInput,
      ingress ?? ''
    );
    const updateFormReviewInput = {
      userName: profile.user.userName,
      userID: selectedContact?.userID,
      year: selectedContact?.calYear,
      week: selectedContact?.calWeek,
      contactID: contactId,
      reviewStatus: ContactStatusOptionsObject.REVIEWED,
      contactDetailsInput,
      isReviewedByCSA: false,
      contactStatus: status,
      formDetails: formDetailsInput,
      inAppFlag: status === ContactStatusOptionsObject.DELIVERED,
      emailFlag: false,
      locale: getLocaleString(),
    };

    try {
      await apiNaws.updateFormReview(
        updateFormReviewInput,
        setFormStatusCode,
        setFormStatusCodeState,
        setExceptionMessage
      );
      if (
        ingress === OUTLIER_INGRESS &&
        isFeatureEnabled(Feature.OUTLIER_MANAGEMENT) &&
        updateFormReviewInput.contactDetailsInput.goodRecommendation ===
          GoodRecommendationOptionsObject.NO &&
        status === FormSaveType.DELIVERED
      ) {
        const writeOutlierExceptionInput =
          apiNaws.prepareQueryForWriteOutlierException(
            requesterUserID, //userId
            profile.user.userName, //deliveredToUsername
            selectedContact?.userID, //deliveredToUserId
            contactId, //contactId
            selectedContact?.calYear, //outlierCalYear
            selectedContact?.calWeek //outlierCalWeek
          );
        await apiNaws.writeOutlierException(
          writeOutlierExceptionInput,
          setOutlierResp,
          setOutlierRespState
        );
      }
      if (status === FormSaveType.DRAFT) {
        setIsSavingDraft(true);

        let redirectUrl = `${urls.contactReview}/${userId}/${contactId}`;

        if (ingress === 'realtimeHMD') {
          redirectUrl += `?ingress=realtimeHMD`;
        }
        if (ingress === OUTLIER_INGRESS) {
          redirectUrl += `?ingress=${OUTLIER_INGRESS}`;
        }
        navigate(renderUrl(redirectUrl), { replace: true });
      }
    } catch (error) {
      console.error('Error saving data', error);
    }
  };

  const stepperLeftColumn = (
    <>
      <Row className={styles.title}>
        <Text type='h400'>
          <FormattedMessage
            id='cs_perfportal_cap_form_title'
            defaultMessage='CAP Review Form'
          />
        </Text>
      </Row>
      <Box>
        <ProgressTracker type='theme' compact={false}>
          {stepperKeys.map((key, index) => (
            <ProgressTrackerStep
              key={key}
              type={
                formState?.stepperProgress?.[key] ?? StepperProgressTypes.FUTURE
              }
              label={
                <Column width={styles.leftCol}>
                  <Text>{getStepperLabel(key)}</Text>
                </Column>
              }
              showLabel
              alert='none'
              trackLength={6}
            ></ProgressTrackerStep>
          ))}
        </ProgressTracker>
      </Box>
    </>
  );

  const activeTenet =
    formState?.pages?.[formState?.currentPage ?? 0]?.type ??
    Object.keys(formState?.stepperProgress ?? {})?.reverse()?.[0];

  const stepperRightColumn = (
    <Box type='outline' spacingInset='400' width='920px' height='auto'>
      <Text type='h300' className={styles.activeHeading}>
        {activeTenet ?? ''}
      </Text>
      {formState?.pages?.[formState?.currentPage ?? 0]?.points.map(point => {
        if (point.question.QUESTION_TYPE === FormComponents.TITLE) {
          return (
            <>
              <Text type='h200' className={styles.activeSubHeading}>
                {intl.formatMessage({
                  id: point.question.QUESTION_TEXT.TRANSLATION_ID,
                  defaultMessage: point.question.QUESTION_TEXT.DEFAULT_MESSAGE,
                })}
              </Text>
            </>
          );
        } else if (point.question.QUESTION_TYPE === FormComponents.DROPDOWN) {
          return (
            <>
              <RenderGenericDropdownsFromData
                question={point.question}
                styles={styles}
                onChange={handleQuestionAnswered(
                  point.question.QUESTION_TEXT.DEFAULT_MESSAGE
                )}
                selected={
                  formState?.questionsAnswered?.[
                    point.question.QUESTION_TEXT.DEFAULT_MESSAGE
                  ]
                }
              />
            </>
          );
        } else if (point.question.QUESTION_TYPE === FormComponents.RADIO) {
          return (
            <>
              <RenderGenericRadioButtonsFromData
                question={point.question}
                selected={
                  formState?.questionsAnswered?.[
                    point.question.QUESTION_TEXT.DEFAULT_MESSAGE
                  ]
                }
                onChange={handleQuestionAnswered(
                  point.question.QUESTION_TEXT.DEFAULT_MESSAGE
                )}
                styles={styles}
                metBar={false}
              />
            </>
          );
        } else if (point.question.QUESTION_TYPE === FormComponents.FREETEXT) {
          return (
            <>
              <RenderGenericTextAreaFromData
                question={point.question}
                selected={
                  formState?.questionsAnswered?.[
                    point.question.QUESTION_TEXT.DEFAULT_MESSAGE
                  ]
                }
                onChange={handleQuestionAnswered(
                  point.question.QUESTION_TEXT.DEFAULT_MESSAGE
                )}
                styles={styles}
                height={2}
                width={'310px'}
              />
            </>
          );
        } else if (point.question.QUESTION_TYPE === FormComponents.AUTOFILL) {
          return (
            <>
              <RenderGenericTextAreaFromData
                question={point.question}
                selected={
                  formState?.questionsAnswered?.[
                    point.question.QUESTION_TEXT.DEFAULT_MESSAGE
                  ]
                }
                onChange={handleQuestionAnswered(
                  point.question.QUESTION_TEXT.DEFAULT_MESSAGE
                )}
                styles={styles}
                height={1}
                width={'340px'}
              />
            </>
          );
        } else if (point.question.QUESTION_TYPE === FormComponents.METBAR) {
          return (
            <>
              <RenderGenericRadioButtonsFromData
                question={point.question}
                selected={
                  formState?.questionsAnswered?.[
                    point.question.QUESTION_TEXT.DEFAULT_MESSAGE
                  ]
                }
                onChange={handleQuestionAnswered(
                  point.question.QUESTION_TEXT.DEFAULT_MESSAGE
                )}
                styles={styles}
                metBar={true}
              />
            </>
          );
        } else {
          return <></>;
        }
      })}
      <div className={styles.rightButtonsContainer}>
        <div className={styles.backButtonContainer}>
          <Button
            type='secondary'
            disabled={formState?.currentPage === 0}
            onClick={handleClickBackButton}
            {...csaFields(
              'button',
              'back',
              `cs_perfportal_cap_back-${formState?.currentPage}`,
              ingress,
              !!ccxDetailsInfo
            )}
          >
            <FormattedMessage
              id='cs_perfportal_cap_back'
              defaultMessage={getDefaultMessageByID(
                CAPFormData?.[0],
                'cs_perfportal_cap_back'
              )}
            />
          </Button>
        </div>
        <div className={styles.nextButtonContainer}>
          {(formState?.currentPage ?? 0) !==
            (formState?.pages?.length ?? 1) - 1 && (
            <Button
              disabled={formState?.nextButtonDisabled}
              onClick={handleClickNextPage}
              {...csaFields(
                'button',
                'next',
                `cs_perfportal_cap_next-${formState?.currentPage}`,
                ingress,
                !!ccxDetailsInfo
              )}
            >
              <FormattedMessage
                id='cs_perfportal_cap_next'
                defaultMessage={getDefaultMessageByID(
                  CAPFormData?.[0],
                  'cs_perfportal_cap_next'
                )}
              />
            </Button>
          )}
        </div>
      </div>
    </Box>
  );

  const isReviewedOrDelivered = contactId
    ? formState?.contactInfo?.contactStatus === FormSaveType.REVIEWED ||
      formState?.contactInfo?.contactStatus === FormSaveType.DELIVERED
    : false;

  let totalPoints = 0;
  for (const p of formState?.pages ?? []) {
    totalPoints += p?.points?.length;
  }

  let requiredQuestions = 0;
  (formState?.pages ?? []).forEach(page => {
    page.points.forEach(point => {
      if (
        point.required &&
        formState?.questionsAnswered?.[
          point.question.QUESTION_TEXT.DEFAULT_MESSAGE
        ]
      ) {
        requiredQuestions++;
      }
    });
  });
  const dropdownItemsComplete =
    formState.dropdownItems?.reduce(
      (allSelected: boolean, item: DropdownItem) => {
        if (item.type === 'contact_review_form_type') return true;
        return allSelected && item.selected !== undefined;
      },
      true
    ) ?? false;
  const radioComplete =
    formState.radioButtonItems?.reduce(
      (allSelected: boolean, item: RadioButtonType) => {
        return allSelected && item.checked !== undefined;
      },
      true
    ) ?? false;

  const canSave =
    requiredQuestions == requiredQuestionCount &&
    dropdownItemsComplete &&
    radioComplete;

  const openFeedbackModal = () => {
    setShowContactReviewFeedbackModal({
      open: true,
      status: FormSaveType.DELIVERED,
    });
  };

  const handleSelectRadioValue = (type: string) => (checked: number) => {
    const items = formState?.radioButtonItems;
    if (items) {
      const index = items.findIndex(t => t.type === type);
      const newItem = { ...items[index], checked };
      items[index] = newItem;
      setFormState(prevFormState => ({
        ...prevFormState,
        radioButtonItems: items,
      }));
    }
  };

  // Form can not be (re)submitted when its state is LOADING or LOADED.
  const isFormLoadingOrLoaded =
    formStatusCodeState === DataState.LOADING ||
    formStatusCodeState === DataState.LOADED;

  const isSaveDraftButtonDisabled =
    isReviewedOrDelivered || isFormLoadingOrLoaded;
  const isDeliverButtonDisabled =
    !canSave || isReviewedOrDelivered || isFormLoadingOrLoaded;
  // CAP review form has no email delivery option, hence we don't have a flag to disable email checkbox here (unlike other forms)

  return (
    <>
      {CAPFormData && (
        <Box className={styles.capFormContainer}>
          <Row className={styles.unorderedListBlockContactInformation}>
            <div className={styles.unorderedListContainer}>
              <ul className={styles.unorderedList}>
                {formState?.contactInfo?.contactDate && (
                  <li data-cy='contact-date'>
                    <FormattedMessage
                      id='cs_perfportal_contact_recom_contact_date'
                      defaultMessage='Contact date'
                    />
                    :{' '}
                    {new Date(
                      formState?.contactInfo?.contactDate
                    ).toLocaleDateString()}
                  </li>
                )}
                {formState?.contactInfo?.capFields?.site && (
                  <li data-cy='site'>
                    <FormattedMessage
                      id='cs_perfportal_cap_site'
                      defaultMessage='Site'
                    />
                    : {formState?.contactInfo?.capFields?.site}
                  </li>
                )}
                {formState?.contactInfo?.capFields?.contactWeek && (
                  <li data-cy='contact-week'>
                    <FormattedMessage
                      id='cs_perfportal_cap_contact_week'
                      defaultMessage='Contact Week'
                    />
                    : {formState?.contactInfo?.capFields?.contactWeek}
                  </li>
                )}
                {formState?.contactInfo?.capFields?.auditWeek && (
                  <li data-cy='audit-week'>
                    <FormattedMessage
                      id='cs_perfportal_cap_audit_week'
                      defaultMessage='Audit Week'
                    />
                    : {formState?.contactInfo?.capFields?.auditWeek}
                  </li>
                )}
                {formState?.contactInfo?.capFields?.flaggedForAbuse && (
                  <li data-cy='flagged-for-abuse'>
                    <FormattedMessage
                      id='cs_perfportal_cap_flagged'
                      defaultMessage='Flagged for Abuse'
                    />
                    : {formState?.contactInfo?.capFields?.flaggedForAbuse}
                  </li>
                )}
                {formState?.contactInfo?.capFields?.contactType && (
                  <li data-cy='contact-type'>
                    <FormattedMessage
                      id='cs_perfportal_cap_contact_type'
                      defaultMessage='Contact Type'
                    />
                    : {formState?.contactInfo?.capFields?.contactType}
                  </li>
                )}
                {formState?.contactInfo?.capFields?.handledDate && (
                  <li data-cy='cap-handled-date'>
                    <FormattedMessage
                      id='cs_perfportal_cap_handled_date'
                      defaultMessage='CAP Handled Date'
                    />
                    : {formState?.contactInfo?.capFields?.handledDate}
                  </li>
                )}
                {formState?.contactInfo?.capFields?.sic1 && (
                  <li data-cy='sic1'>
                    <FormattedMessage
                      id='cs_perfportal_cap_sic1'
                      defaultMessage='SIC1'
                    />
                    : {formState?.contactInfo?.capFields?.sic1}
                  </li>
                )}
                {formState?.contactInfo?.capFields?.sic2 && (
                  <li data-cy='sic2'>
                    <FormattedMessage
                      id='cs_perfportal_cap_sic2'
                      defaultMessage='SIC2'
                    />
                    : {formState?.contactInfo?.capFields?.sic2}
                  </li>
                )}
                {formState?.contactInfo?.capFields?.sic3 && (
                  <li data-cy='sic3'>
                    <FormattedMessage
                      id='cs_perfportal_cap_sic3'
                      defaultMessage='SIC3'
                    />
                    : {formState?.contactInfo?.capFields?.sic3}
                  </li>
                )}
                {formState?.contactInfo?.capFields?.sic4 && (
                  <li data-cy='sic4'>
                    <FormattedMessage
                      id='cs_perfportal_cap_sic4'
                      defaultMessage='SIC4'
                    />
                    : {formState?.contactInfo?.capFields?.sic4}
                  </li>
                )}
                {formState?.contactInfo?.capFields?.sic1_2 && (
                  <li data-cy='sic1_2'>
                    <FormattedMessage
                      id='cs_perfportal_cap_sic1_2'
                      defaultMessage='SIC1_2'
                    />
                    : {formState?.contactInfo?.capFields?.sic1_2}
                  </li>
                )}
                {formState?.contactInfo?.capFields?.rsprUtilization && (
                  <li data-cy='rsprUtilization'>
                    <FormattedMessage
                      id='cs_perfportal_cap_rspr_utilization'
                      defaultMessage='RSPR Utilization'
                    />
                    : {formState?.contactInfo?.capFields?.rsprUtilization}
                  </li>
                )}
                {formState?.contactInfo?.capFields
                  ?.rsprPolicyRecommendation && (
                  <li data-cy='rsprPolicyRecommendation'>
                    <FormattedMessage
                      id='cs_perfportal_cap_rspr_policy_recommendation'
                      defaultMessage='RSPR Policy Recommendation'
                    />
                    :{' '}
                    {
                      formState?.contactInfo?.capFields
                        ?.rsprPolicyRecommendation
                    }
                  </li>
                )}
              </ul>
            </div>
          </Row>
          <Row>
            <RenderDropdownsFromData
              data={CAPFormData?.[0]}
              formPrefix=''
              dropdownSectionID='GENERAL_PARAMETERS_DROPDOWNS'
              styles={styles}
              handleSelectDropdownValue={handleSelectDropdownValue}
              formState={formState}
              setFormState={setFormState}
            />
          </Row>
          <Row>
            <RadioButtonsFromData
              data={CAPFormData?.[0]}
              formPrefix=''
              radioSectionID='CAP_GENERAL_PARAMETERS_RADIO'
              styles={styles}
              handleSelectRadioValue={handleSelectRadioValue}
              formState={formState}
              setFormState={setFormState}
              selectedValue={undefined}
              heading={undefined}
            />
          </Row>
          {formType && (
            <>
              <Row className={styles.formStepperContainer}>
                <Column className={styles.leftCol}>{stepperLeftColumn}</Column>
                <Column className={styles.rightCol}>
                  {stepperRightColumn}
                </Column>
              </Row>
              <div className={styles.buttonsContainer}>
                <Button
                  onClick={handleSaveAsDraft}
                  type='secondary'
                  className={styles.saveButton}
                  disabled={isSaveDraftButtonDisabled}
                  {...csaFields(
                    'button',
                    'save as draft',
                    'cs_perfportal_cap_draft',
                    ingress,
                    !!ccxDetailsInfo
                  )}
                >
                  <FormattedMessage
                    id='cs_perfportal_cap_draft'
                    defaultMessage={
                      grabExtraFieldByID(
                        CAPFormData?.[0],
                        'cs_perfportal_cap_draft'
                      )?.DEFAULT_MESSAGE
                    }
                  />
                </Button>
                <Button
                  data-testid='deliver-button'
                  onClick={openFeedbackModal}
                  className={styles.saveButton}
                  disabled={isDeliverButtonDisabled}
                  {...csaFields(
                    'button',
                    'deliver',
                    'cs_perfportal_cap_deliver',
                    ingress,
                    !!ccxDetailsInfo
                  )}
                >
                  <FormattedMessage
                    id='cs_perfportal_cap_deliver'
                    defaultMessage={
                      grabExtraFieldByID(
                        CAPFormData?.[0],
                        'cs_perfportal_cap_deliver'
                      )?.DEFAULT_MESSAGE
                    }
                  />
                </Button>
              </div>
            </>
          )}
        </Box>
      )}
      {!CAPFormData && <Loader data-testid='loader' />}
      {contactId && (
        <ContactReviewFeedbackModal
          open={showContactReviewFeedbackModal?.open}
          parentValues={
            contactList?.filter(c => c.contactID === contactId)?.[0] ?? {}
          }
          parentUserName={profile.user?.userName}
          parentUserID={userId ?? ''}
          onFeedbackSubmitted={handleFeedbackSubmitted}
          contactTab={ContactViewTabObject.RECOMMENDED}
          contactCategory='Corrective'
          ingress={ingress}
        />
      )}
      <TimeoutToast
        show={isAdmin && showErrorToast}
        message={exceptionMessage}
        duration={3000}
        type='error'
        onClose={(): void => {
          setShowErrorToast(false);
        }}
      />
      <DraftSavedToast
        show={showDraftSavedToast}
        onClose={() => setShowDraftSavedToast(false)}
      />
    </>
  );
};

export default CAPReviewForm;
